/**
 * Public Scan Page
 *
 * STRICT MVVM: Zero logic, only rendering based on ViewModel state
 *
 * Displays NFC scan context for public viewers:
 * - Product information (title, image)
 * - Owner profile header (with optional View Profile button)
 * - Privacy-aware: View Profile button only appears for public items
 *
 * Data Flow: NfcScanPage wrapper fetches → Redux → ViewModel → PublicScanPage renders
 *
 * Story 3.7.c - Public scan page with privacy-aware profile navigation
 */

import { useTranslation } from "react-i18next";
import { usePublicScanViewModel } from "@/viewModels/usePublicScanViewModel";
import {
  ErrorState,
  ProfileHeader,
  PublicCollectionItem,
} from "@/ui/components";

/**
 * PublicScanPage Component
 *
 * Pure view component - all logic in usePublicScanViewModel.
 *
 * IMPORTANT: Data is fetched by NfcScanPage wrapper, not this component.
 * This component reads from Redux via ViewModel (no params needed).
 *
 * Responsive Design:
 * - Mobile (< 768px): Full width with 32px horizontal padding
 * - Tablet+ (≥ 768px): 700px max-width (md:max-w-[43.75rem]), centered
 * - Consistent pattern across loading/error/success states
 */
export default function PublicScanPage() {
  const { t } = useTranslation();

  // ViewModel - all business logic lives here (reads from Redux)
  const vm = usePublicScanViewModel();

  // --- Loading State ---
  if (vm.loading) {
    return (
      <div
        className="container mx-auto px-8 py-8 w-full md:max-w-[43.75rem]"
        role="status"
        aria-live="polite"
        aria-label={t("common.loading", "Loading...")}
      >
        {/* Product Image Skeleton */}
        <div className="animate-pulse">
          <div className="w-full aspect-square bg-gray-100 rounded-[0.3125rem] mb-6" />

          {/* Profile Header Skeleton */}
          <div className="flex items-center gap-4 mb-6">
            <div className="size-24 bg-gray-100 rounded-[0.3125rem]" />
            <div className="flex-1">
              <div className="h-6 bg-gray-100 rounded w-3/4 mb-2" />
              <div className="h-8 bg-gray-100 rounded w-32" />
            </div>
          </div>

          {/* Product Info Skeleton */}
          <div className="flex flex-col gap-2">
            <div className="h-5 bg-gray-100 rounded w-1/3" />
            <div className="h-6 bg-gray-100 rounded w-full" />
            <div className="h-6 bg-gray-100 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (vm.error) {
    return (
      <div className="container mx-auto px-8 py-8 w-full md:max-w-[43.75rem]">
        <ErrorState
          message={vm.error}
          // No onRetry - user must re-scan NFC tag physically
        />
      </div>
    );
  }

  // --- Success State: Display Scan Context ---
  return (
    <div className="container mx-auto px-8 py-8 w-full md:max-w-[43.75rem]">
      {/* Owner Profile Header (only if username exists) */}
      {vm.ownerUsername && (
        <div className="mb-10">
          <ProfileHeader
            username={vm.ownerUsername}
            avatar={vm.ownerAvatar || undefined}
            onViewProfile={vm.handleViewProfile}
          />
        </div>
      )}

      {/* Product Card */}
      {vm.productImage && vm.productTitle && (
        <div className="mb-6">
          <PublicCollectionItem
            productImage={vm.productImage}
            productName={vm.productTitle}
            productNumber={vm.productNumber}
            totalProductNumber={vm.totalProductNumber}
            shopifyUrl={vm.shopifyUrl}
            partnerLink={vm.partnerLink}
          />
        </div>
      )}
    </div>
  );
}
